import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { TipoUsuario } from '../../types';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  validateRequired,
  validateEmail,
  validateMinLength,
  validateMaxLength,
  validatePasswordMatch,
  combineValidations,
} from '../../utils/validations';

const Cadastro: React.FC = () => {
  usePageTitle('Cadastro');

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [nomeEstabelecimento, setNomeEstabelecimento] = useState('');
  const [lojaFisica, setLojaFisica] = useState(false);
  const [quantidadeMesas, setQuantidadeMesas] = useState('0');
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated && user) {
      navigate(
        user.tipo === TipoUsuario.Administrador ? '/admin/estabelecimentos' : '/dashboard',
        { replace: true }
      );
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (senha !== confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/usuarios/registrar', {
        nome,
        email,
        senha,
        nomeEstabelecimento,
        lojaFisica,
        quantidadeMesas: lojaFisica ? parseInt(quantidadeMesas) || 0 : 0,
      });

      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success(`Bem-vindo, ${userData.nome}! Sua conta foi criada.`);
      window.location.href = '/dashboard';
    } catch (error: any) {
      const message = error.response?.data?.error
        || error.response?.data?.[0]?.errorMessage
        || 'Erro ao criar conta. Tente novamente.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-neutral-100 p-8 w-full max-w-lg animate-fadeIn">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Gestta" className="h-12 mx-auto mb-3" />
          <p className="text-neutral-500 text-sm font-medium">
            Crie sua conta e comece a gerenciar seu negócio.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Seção: Dados pessoais */}
          <div>
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wider mb-3">
              Seus dados
            </h3>
            <div className="space-y-3">
              <Input
                label="Nome completo *"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Seu nome completo"
                onValidate={combineValidations(
                  validateRequired('Nome'),
                  validateMaxLength(200, 'Nome')
                )}
                validateOnBlur
              />

              <Input
                label="Email *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                onValidate={combineValidations(
                  validateRequired('Email'),
                  validateEmail
                )}
                validateOnBlur
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Senha *"
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Min. 6 caracteres"
                  onValidate={combineValidations(
                    validateRequired('Senha'),
                    validateMinLength(6, 'Senha')
                  )}
                  validateOnBlur
                />

                <Input
                  label="Confirmar senha *"
                  type="password"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  placeholder="Repita a senha"
                  onValidate={validatePasswordMatch(senha)}
                  validateOnBlur
                />
              </div>
            </div>
          </div>

          {/* Divisor */}
          <div className="border-t border-neutral-200" />

          {/* Seção: Estabelecimento */}
          <div>
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wider mb-3">
              Seu estabelecimento
            </h3>
            <div className="space-y-3">
              <Input
                label="Nome do estabelecimento *"
                type="text"
                value={nomeEstabelecimento}
                onChange={(e) => setNomeEstabelecimento(e.target.value)}
                placeholder="Ex: Doces da Maria"
                onValidate={combineValidations(
                  validateRequired('Nome do estabelecimento'),
                  validateMaxLength(200, 'Nome do estabelecimento')
                )}
                validateOnBlur
              />

              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lojaFisica}
                    onChange={(e) => {
                      setLojaFisica(e.target.checked);
                      if (!e.target.checked) setQuantidadeMesas('0');
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500" />
                </label>
                <span className="text-sm font-medium text-neutral-700">
                  Atendo em loja física
                </span>
              </div>

              {lojaFisica && (
                <div className="animate-fadeIn">
                  <Input
                    label="Quantidade de mesas"
                    type="number"
                    value={quantidadeMesas}
                    onChange={(e) => setQuantidadeMesas(e.target.value)}
                    placeholder="0"
                    min="0"
                    helperText="Informe 0 se não trabalha com mesas"
                  />
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Criando conta...' : 'Criar minha conta'}
          </Button>
        </form>

        <p className="text-center text-sm text-neutral-500 mt-5">
          Já tem conta?{' '}
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-700 font-semibold hover:underline"
          >
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Cadastro;
